import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const STATUS_CONFIG = {
  pending: { color: 'text-warning', bg: 'bg-warning-100', icon: 'Clock', label: 'Pending' },
  driver_assigned: { color: 'text-blue-500', bg: 'bg-blue-50', icon: 'UserCheck', label: 'Assigned' },
  picked_up: { color: 'text-secondary', bg: 'bg-secondary-100', icon: 'Navigation', label: 'En Route' },
  delivered: { color: 'text-success', bg: 'bg-success-100', icon: 'CheckCircle', label: 'Delivered' },
  cancelled: { color: 'text-error', bg: 'bg-error-100', icon: 'XCircle', label: 'Cancelled' },
};

const OrderCard = ({ order, onFlagUrgent, onUpdateStatus }) => {
  const [expanded, setExpanded] = useState(false);
  const statusCfg = STATUS_CONFIG?.[order?.status] || STATUS_CONFIG?.pending;

  const formatTime = (ts) => {
    if (!ts) return '—';
    return new Date(ts)?.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`border rounded-xl overflow-hidden transition-all duration-200 ${
      order?.isUrgent ? 'border-error/50 bg-error-50/30' : 'border-border bg-surface'
    }`}>
      {/* Urgent Banner */}
      {order?.isUrgent && (
        <div className="flex items-center space-x-2 px-3 py-1.5 bg-error text-white text-xs font-semibold">
          <Icon name="AlertTriangle" size={12} />
          <span>URGENT PRIORITY</span>
        </div>
      )}

      {/* Card Header */}
      <div
        className="flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-surface-hover transition-colors duration-200"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center space-x-2 min-w-0">
          <Icon name={order?.serviceType === 'delivery' ? 'Package' : 'Car'} size={15} className="text-primary flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-text-primary truncate">{order?.orderNumber}</p>
            <p className="text-xs text-text-muted truncate">{order?.customer?.fullName || 'Walk-in'}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          <span className={`flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg?.bg} ${statusCfg?.color}`}>
            <Icon name={statusCfg?.icon} size={10} />
            <span>{statusCfg?.label}</span>
          </span>
          <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-text-muted" />
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-border/50">
          {/* Route */}
          <div className="mt-2 space-y-1.5">
            <div className="flex items-start space-x-2">
              <div className="w-2.5 h-2.5 rounded-full bg-success mt-1 flex-shrink-0" />
              <p className="text-xs text-text-secondary leading-relaxed">{order?.pickupAddress}</p>
            </div>
            <div className="ml-1 w-0.5 h-3 bg-border ml-1" />
            <div className="flex items-start space-x-2">
              <div className="w-2.5 h-2.5 rounded-full bg-error mt-1 flex-shrink-0" />
              <p className="text-xs text-text-secondary leading-relaxed">{order?.dropoffAddress}</p>
            </div>
          </div>

          {/* Driver & Time */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-surface-hover rounded-lg p-2">
              <p className="text-xs text-text-muted">Driver</p>
              <p className="text-xs font-semibold text-text-primary mt-0.5">
                {order?.driver?.name || 'Unassigned'}
              </p>
              {order?.driver?.licensePlate && (
                <p className="text-xs text-text-muted">{order?.driver?.licensePlate}</p>
              )}
            </div>
            <div className="bg-surface-hover rounded-lg p-2">
              <p className="text-xs text-text-muted">Created</p>
              <p className="text-xs font-semibold text-text-primary mt-0.5">{formatTime(order?.createdAt)}</p>
              <p className="text-xs text-text-muted">KES {parseFloat(order?.totalAmount || 0)?.toFixed(0)}</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            {!order?.isUrgent && order?.status !== 'delivered' && order?.status !== 'cancelled' && (
              <button
                onClick={() => onFlagUrgent?.(order?.id)}
                className="flex items-center space-x-1 px-2.5 py-1.5 text-xs font-medium text-error border border-error/30 rounded-lg hover:bg-error-50 transition-colors duration-200"
              >
                <Icon name="AlertTriangle" size={12} />
                <span>Flag Urgent</span>
              </button>
            )}
            {order?.status === 'pending' && (
              <button
                onClick={() => onUpdateStatus?.(order?.id, 'driver_assigned')}
                className="flex items-center space-x-1 px-2.5 py-1.5 text-xs font-medium text-primary border border-primary/30 rounded-lg hover:bg-primary-50 transition-colors duration-200"
              >
                <Icon name="UserCheck" size={12} />
                <span>Assign</span>
              </button>
            )}
            {order?.status === 'driver_assigned' && (
              <button
                onClick={() => onUpdateStatus?.(order?.id, 'picked_up')}
                className="flex items-center space-x-1 px-2.5 py-1.5 text-xs font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors duration-200"
              >
                <Icon name="Navigation" size={12} />
                <span>Mark Picked Up</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ActiveOrdersList = ({ orders = [], onFlagUrgent, onUpdateStatus, filters, onFilterChange }) => {
  const urgentCount = orders?.filter(o => o?.isUrgent)?.length;

  return (
    <div className="flex flex-col h-full bg-surface rounded-xl border border-border overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-primary border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="List" size={18} color="#ffd700" />
            <span className="text-sm font-semibold text-white">Active Orders</span>
            <span className="px-2 py-0.5 bg-accent text-primary text-xs font-bold rounded-full">
              {orders?.length}
            </span>
          </div>
          {urgentCount > 0 && (
            <div className="flex items-center space-x-1 px-2 py-0.5 bg-error rounded-full">
              <Icon name="AlertTriangle" size={11} color="white" />
              <span className="text-white text-xs font-bold">{urgentCount} Urgent</span>
            </div>
          )}
        </div>
      </div>
      {/* Filters */}
      <div className="px-3 py-2 border-b border-border bg-surface-hover flex items-center space-x-2 overflow-x-auto">
        {['all', 'pending', 'driver_assigned', 'picked_up']?.map(status => (
          <button
            key={status}
            onClick={() => onFilterChange?.('status', status)}
            className={`px-2.5 py-1 text-xs rounded-full font-medium whitespace-nowrap transition-colors duration-200 ${
              filters?.status === status
                ? 'bg-primary text-white' :'bg-surface text-text-secondary border border-border hover:border-primary/40'
            }`}
          >
            {status === 'all' ? 'All' : STATUS_CONFIG?.[status]?.label || status}
          </button>
        ))}
        <div className="w-px h-4 bg-border flex-shrink-0" />
        {['all', 'ride', 'delivery']?.map(type => (
          <button
            key={type}
            onClick={() => onFilterChange?.('serviceType', type)}
            className={`px-2.5 py-1 text-xs rounded-full font-medium whitespace-nowrap transition-colors duration-200 ${
              filters?.serviceType === type
                ? 'bg-accent text-primary' :'bg-surface text-text-secondary border border-border hover:border-accent/40'
            }`}
          >
            {type === 'all' ? 'All Types' : type?.charAt(0)?.toUpperCase() + type?.slice(1)}
          </button>
        ))}
      </div>
      {/* Orders List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {orders?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-center">
            <Icon name="Inbox" size={32} className="text-text-muted mb-2" />
            <p className="text-sm text-text-muted">No orders match filters</p>
          </div>
        ) : (
          orders?.map(order => (
            <OrderCard
              key={order?.id}
              order={order}
              onFlagUrgent={onFlagUrgent}
              onUpdateStatus={onUpdateStatus}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default ActiveOrdersList;
